import type { Point } from 'chart.js';
import { Util } from '@libs/utilities/util';
import { Stats } from '@libs/utilities/stats';

import { IGoogleSearchVolumeValue } from '../../../constants/research';


export class SearchVolumeHelperService {

	//** Configurations */
	private readonly AD_RECOMMENDATION_PERCENTILE	: number = 80;
	private readonly MIN_SV_HISTORY_YEAR			: number = 2004;


	/**------------------------------------------------------
	 * Search Volume Mapping
	 */

	//** Get Volume from History Data */
	getHistoryVolume(svHistoryList: IGoogleSearchVolumeValue[]): number[] {
		return svHistoryList.map((svHistory: IGoogleSearchVolumeValue) => Number(svHistory.volume));
	}

	//** Get Date from History Data */
	getHistoryTimestamp(svHistoryList: IGoogleSearchVolumeValue[]): Date[] {
		return svHistoryList.map((svHistory: IGoogleSearchVolumeValue) => new Date(svHistory.year, svHistory.month));
	}

	//** Get Average Volume */
	getAvgSvHistory(svHistoryList: IGoogleSearchVolumeValue[]): number {
		return this.getHistoryVolume(svHistoryList).reduce((x: number, y: number) => x + y, 0) / svHistoryList.length;
	}
	getMedianSvHistory(svHistoryList: IGoogleSearchVolumeValue[]): number {
		return Stats.Basic.median(this.getHistoryVolume(svHistoryList));
	}


	/**------------------------------------------------------
	 * Chart Helpers
	 */

	//** Prepare SV Chart Data */
	prepareHistoryChartData(svHistoryList: IGoogleSearchVolumeValue[]): Point[] {

		//0 - map timestamp to x and volume to y
		const chartData: Point[] = svHistoryList.map((svHistory: IGoogleSearchVolumeValue) => ({
			x	: new Date(svHistory.year, svHistory.month).getTime(),
			y	: svHistory.volume
		}));

		//1 - return chart data
		return chartData;
	}

	//** Prepare SV Annual Chart Data */
	prepareAnnualHistoryChartData(svHistoryList: IGoogleSearchVolumeValue[], monthDuration: number = 12): Point[] {

		//0 - get annual history list
		const annualHistoryList: IGoogleSearchVolumeValue[] = this.getHistoryList(svHistoryList, monthDuration);

		//1 - return the prepared chart data
		return this.prepareHistoryChartData(annualHistoryList);
	}


	/**------------------------------------------------------
	 * History Helper
	 */

	//** Get Previous Years Data */
	getPreviousHistoryData(svHistoryList: IGoogleSearchVolumeValue[], month: number, year: number): IGoogleSearchVolumeValue {

		//0 - get sv history with same month and year
		const prevSvHistoryList = svHistoryList.find((svHistory: IGoogleSearchVolumeValue) => svHistory.month === month && svHistory.year === year && svHistory.volume !== 0);

		//1 - return filtered history list if not empty
		if (prevSvHistoryList && !Util.Object.isEmpty(prevSvHistoryList)) return prevSvHistoryList;

		//2 - return empty if no history data found
		if (year < this.MIN_SV_HISTORY_YEAR) return {
			month	: month,
			year 	: year,
			volume	: 0
		};

		//3 - recursive call to fetch last years data
		return this.getPreviousHistoryData(svHistoryList, month, year - 1);
	}

	//** Get trend for monthly duration */
	getTrend(history: IGoogleSearchVolumeValue[], monthDuration: number): number {

		//0 - get current and past date
		const currentDate	: Date = new Date();
		const pastDate		: Date = Util.Date.pastDateByMonth(monthDuration);

		//1 - get current and past search volumes
		const currentSv		: number = this.getPreviousHistoryData(history, currentDate.getMonth(), currentDate.getFullYear()).volume;
		const pastSv		: number = this.getPreviousHistoryData(history, pastDate.getMonth(), pastDate.getFullYear()).volume;

		//2 - calculate trend based on current and past search volumes
		// > WORKAROUND: 0 added because in case 0 pastSV this will return NaN i.e invalid
		const trend			: number = (currentSv - pastSv) / pastSv;
		return !isNaN(trend) && isFinite(trend) ? Util.Number.roundDouble(trend) : 0;
	}

	//** Get Search volume history data */
	getHistoryList(svHistoryList: IGoogleSearchVolumeValue[], monthDuration: number): IGoogleSearchVolumeValue[] {

		//0 - setup month range and annual history list
		const monthRange		 : number[] 			= Array.from(Array(monthDuration + 1).keys());
		const annualSvHistoryList: IGoogleSearchVolumeValue[] = [];

		//1 - iterate through the month range and get sv history data for every month
		for (const monthDiff of monthRange) {

			//a. get past date with month difference
			const currDate	: Date		= new Date(new Date().setDate(1));
			const pastDate	: Date 		= Util.Date.pastDateByMonth(monthDiff, currDate);
			const pastMonth	: number 	= pastDate.getMonth();
			const pastYear	: number 	= pastDate.getFullYear();

			//b. get previous history data
			const monthlyHistoryList: IGoogleSearchVolumeValue = this.getPreviousHistoryData(svHistoryList, pastMonth, pastYear);
			const updatedMonthlyList: IGoogleSearchVolumeValue = {
				volume	: monthlyHistoryList.volume,
				month	: pastMonth,
				year	: pastYear
			};
			annualSvHistoryList.push(updatedMonthlyList);
		}

		//1 - interpolate history data for missing values
		const interpolatedHistoryData: IGoogleSearchVolumeValue[] = this.interpolateHistoryData(annualSvHistoryList);

		//2 - return annual history list
		return interpolatedHistoryData;
	}

	//** Compute Ad Recommendation */
	getAdRecommendationChartData(historySvData: IGoogleSearchVolumeValue[]): Point[] {

		//0 - extrapolate sv data
		const adRecommendationChartData	: Point[] = [];
		const extrapolatedHistoryData	: IGoogleSearchVolumeValueDate[] = historySvData.map((svHistory: IGoogleSearchVolumeValue) => ({ month: svHistory.month, year: svHistory.year }));
		const uniqueHistoryData			: IGoogleSearchVolumeValueDate[] = Util.Array.unique(extrapolatedHistoryData, (x: IGoogleSearchVolumeValueDate, y: IGoogleSearchVolumeValueDate) => x.month === y.month && x.year === y.year);

		//1 - get amazon sv percentile
		const historySvList				: number[] 	= this.getHistoryVolume(historySvData).sort((x: number, y: number) => x - y);
		const historySvPercentile		: number 	= historySvList[Math.ceil(historySvList.length * this.AD_RECOMMENDATION_PERCENTILE / 100)];

		//2 - iterate through all sv data and compute ad recommendation data
		for (const historyData of uniqueHistoryData) {

			//a. get sv data
			const historySv: number = historySvData.find((historySvHistory: IGoogleSearchVolumeValue) => historySvHistory.month === historyData.month && historySvHistory.year === historyData.year)?.volume || 0;

			//b. compute ad recommendation and crate the chart point
			const adRecommendationPoint: Point = { x: Util.Date.pastDateByMonth(0, new Date(historyData.year, historyData.month)).getTime(), y: historySvPercentile <= historySv ? historySv : 0 };

			//c. add the chart point to the chart data
			adRecommendationChartData.push(adRecommendationPoint);
		}

		// - return ad recommendation chart data
		return adRecommendationChartData;
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */

	//** Interpolate invalid history data */
	private interpolateHistoryData(historyDataList: IGoogleSearchVolumeValue[]): IGoogleSearchVolumeValue[] {

		//0 - iterate through all history and check for invalid entires
		for (const [index, historyData] of Object.entries(historyDataList)) {

			//a. check if volume data is valid
			if (historyData?.volume > 0) continue;

			//b. interpolate volume data by taking avg
			const previousSv: number = this.getPreviousSearchVolume(historyDataList, Number(index) - 1);
			const nextSv	: number = this.getNextSearchVolume(historyDataList, Number(index) + 1);
			historyData.volume = (previousSv + nextSv) / 2;
		}

		//1 - return updated history data
		return historyDataList;
	}

	//** Get valid previous search volume */
	getPreviousSearchVolume(historyDataList: IGoogleSearchVolumeValue[], currentIndex: number): number {

		//0 - if volume is valid return volume
		if (historyDataList[currentIndex]?.volume > 0) return historyDataList[currentIndex].volume;

		//1 - if no more data left return 0
		if (currentIndex <= 0) return 0;

		//2 - recursive calls with directional decrement
		return this.getPreviousSearchVolume(historyDataList, currentIndex - 1);
	}


	//** Get valid previous search volume */
	getNextSearchVolume(historyDataList: IGoogleSearchVolumeValue[], currentIndex: number): number {

		//0 - if volume is valid return volume
		if (historyDataList[currentIndex]?.volume > 0) return historyDataList[currentIndex].volume;

		//1 - if no more data left return 0
		if (currentIndex >= historyDataList.length - 1) return 0;

		//2 - recursive calls with directional increment
		return this.getNextSearchVolume(historyDataList, currentIndex + 1);
	}
}


//** Interface ---------------------------------- */
interface IGoogleSearchVolumeValueDate {
	month	: number;
	year	: number;
}
